import type { PreparedSeries } from "../prepare-series";

export type SeriesReading = { timeMs: number; value: number };

/** First index whose time is at or after timeMs. */
function lowerBound(points: PreparedSeries["points"], timeMs: number): number {
    let low = 0;
    let high = points.length;
    while (low < high) {
        const mid = Math.floor((low + high) / 2);
        if (points[mid][0].getTime() < timeMs) low = mid + 1;
        else high = mid;
    }
    return low;
}

/**
 * Finds the bar the pointer is over. Each bar's time is its left edge, so going by the closest time
 * would switch to the next bar once the pointer passes the middle of this one.
 */
function bucketAt(points: PreparedSeries["points"], timeMs: number, bucketMs: number): SeriesReading | null {
    // The bucket that starts at the pointer, or else the one before it.
    const at = lowerBound(points, timeMs);
    const index = points[at]?.[0].getTime() === timeMs ? at : at - 1;
    const bucket = points[index];
    if (!bucket || bucket[1] === null || timeMs >= bucket[0].getTime() + bucketMs) return null;
    return { timeMs: bucket[0].getTime(), value: bucket[1] };
}

/**
 * Nearest reading by time, the rule echarts' tooltip uses, but no further away than staleAfterMs so a
 * stream that went quiet reads as missing. A bucketed bar instead reads the bucket under the pointer.
 */
export function findNearestReading(
    points: PreparedSeries["points"],
    timeMs: number,
    { seriesType, bucketMs }: Pick<PreparedSeries, "seriesType" | "bucketMs">,
    staleAfterMs: number,
): SeriesReading | null {
    if (seriesType === "bar" && bucketMs > 0) return bucketAt(points, timeMs, bucketMs);

    const at = lowerBound(points, timeMs);
    let beforeIndex = at - 1;
    let afterIndex = at;

    // A line's only nulls mark stale gaps. Step over them so the readings either side still count.
    if (seriesType === "line") {
        while (beforeIndex >= 0 && points[beforeIndex][1] === null) beforeIndex--;
        while (afterIndex < points.length && points[afterIndex][1] === null) afterIndex++;
    }

    const before = points[beforeIndex];
    const after = points[afterIndex];
    let nearest = before ?? after;
    if (before && after) {
        nearest = timeMs - before[0].getTime() <= after[0].getTime() - timeMs ? before : after;
    }

    if (!nearest || nearest[1] === null) return null;

    const nearestMs = nearest[0].getTime();
    if (Math.abs(timeMs - nearestMs) > staleAfterMs) return null;
    return { timeMs: nearestMs, value: nearest[1] };
}

/** The newest reading. Bars keep empty trailing buckets as nulls, so scan back past them. */
export function lastReading(points: PreparedSeries["points"]): SeriesReading | null {
    for (let index = points.length - 1; index >= 0; index--) {
        const [time, value] = points[index];
        if (value !== null) return { timeMs: time.getTime(), value };
    }
    return null;
}
